import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Sparkles } from "lucide-react";

type Suggestion = {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  shared_krews: number;
  reliability_score: number;
};

interface Props {
  krewId: string;
  isCreator: boolean;
  onInvited?: () => void;
}

const SmartSuggestions = ({ krewId, isCreator, onInvited }: Props) => {
  const { toast } = useToast();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState<string | null>(null);

  useEffect(() => {
    if (!isCreator) { setLoading(false); return; }
    supabase.rpc("suggested_krew_members", { p_krew_id: krewId, p_limit: 8 }).then(({ data, error }) => {
      if (!error && data) setSuggestions(data as Suggestion[]);
      setLoading(false);
    });
  }, [krewId, isCreator]);

  if (!isCreator) return null;
  if (loading) return <div className="h-24 animate-pulse rounded-xl bg-card" />;
  if (suggestions.length === 0) return null;

  const invite = async (userId: string) => {
    setInviting(userId);
    const { error } = await supabase.from("krew_members").insert({ krew_id: krewId, user_id: userId, status: "invited" });
    if (error) {
      toast({ title: "Couldn't invite", description: error.message, variant: "destructive" });
    } else {
      await supabase.from("notifications").insert({
        user_id: userId,
        type: "info",
        title: "You've been invited to a Krew",
        message: "Open it to commit your spot.",
        link: `/krew/${krewId}`,
      });
      setSuggestions(suggestions.filter(s => s.user_id !== userId));
      toast({ title: "Invited!", description: "They'll see it in their notifications." });
      onInvited?.();
    }
    setInviting(null);
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-secondary" />
        <h3 className="font-semibold text-foreground">Suggested by Krew AI</h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">People you've been out with before, ranked by reliability.</p>
      <div className="space-y-2">
        {suggestions.map((s) => (
          <div key={s.user_id} className="flex items-center gap-3 rounded-xl border border-border bg-background/40 p-3">
            {s.avatar_url ? (
              <img src={s.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/20 text-sm font-bold text-primary">
                {s.display_name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{s.display_name}</p>
              <p className="text-xs text-muted-foreground">{s.shared_krews} shared Krew{s.shared_krews !== 1 ? "s" : ""} · {Math.round(Number(s.reliability_score))}% reliable</p>
            </div>
            <button
              onClick={() => invite(s.user_id)}
              disabled={inviting === s.user_id}
              className="flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
            >
              <UserPlus className="h-3.5 w-3.5" /> Invite
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SmartSuggestions;